// Origineel: client/imports/ui/Tasks/SharedTasks/SharedTaskState.tsx (de provider staat in ./Providers.tsx)
import { createContext, useContext, useEffect, type Dispatch, type SetStateAction } from 'react';
import { gedeeldeKeuzeLoopt, type Player } from '@ame/shared';

/** De gedeelde taken van één speler. */
export type TasksPerPlayer = { id: string; tasks: string[] };

/**
 * `showItems`: alle spelers hebben hun taken gespeeld, dus het gezamenlijke kiezen kan
 * beginnen (`gedeeldeKeuzeLoopt`). `allTasksPerPlayer` en `allTasks` zijn dan hun gedeelde taken (per speler en
 * alles bij elkaar); anders zijn ze leeg.
 */
export type SharedTaskStateData = {
  showItems: boolean;
  allTasksPerPlayer: TasksPerPlayer[];
  allTasks: string[];
};

export type SharedTaskStateWaarde = {
  state: SharedTaskStateData;
  setShowItems: Dispatch<SetStateAction<SharedTaskStateData>>;
};

export const SharedTaskStateContext = createContext<SharedTaskStateWaarde | undefined>(undefined);

export const useSharedTaskState = (): SharedTaskStateWaarde => {
  const waarde = useContext(SharedTaskStateContext);
  if (!waarde) throw new Error('useSharedTaskState must be used within a SharedTaskStateContext');
  return waarde;
};

/**
 * Houdt de gedeelde staat bij uit de spelers, zoals het effect in `SharedTaskView` van het
 * origineel: pas als elke speler taken heeft gespeeld, begint het gezamenlijke kiezen.
 *
 * Niet in het origineel: een plek zonder naam telt niet mee. Daar moest elke plek inleggen,
 * dus een spel voor vier dat met drie gespeeld werd, kwam nooit bij het kiezen. De server
 * gebruikt dezelfde regel (`gedeeldeKeuzeLoopt` in `serverSelectTask`).
 *
 * `TaskOverview` zette in het origineel alleen `showItems` en liet de lijsten van de vorige
 * ronde staan; die waren dan één beeld lang te zien. Hier rekenen beide schermen alles uit.
 */
export function useVolgGedeeldeTaken(players: Player[] | undefined): void {
  const { setShowItems } = useSharedTaskState();
  // Als tekst, omdat elke nieuwe staat een nieuwe lijst geeft.
  const gedeeldeTaken = JSON.stringify(
    players?.map(({ _id, name, sharedTasks }) => ({ _id, name, sharedTasks })) ?? [],
  );

  useEffect(() => {
    const spelers = JSON.parse(gedeeldeTaken) as Pick<Player, '_id' | 'name' | 'sharedTasks'>[];
    const ingebracht = spelers.filter(({ sharedTasks }) => sharedTasks.length > 0);
    setShowItems(
      gedeeldeKeuzeLoopt(spelers)
        ? {
            showItems: true,
            allTasksPerPlayer: ingebracht.map(({ _id, sharedTasks }) => ({
              id: _id,
              tasks: sharedTasks,
            })),
            allTasks: ingebracht.flatMap(({ sharedTasks }) => sharedTasks),
          }
        : { showItems: false, allTasksPerPlayer: [], allTasks: [] },
    );
  }, [gedeeldeTaken, setShowItems]);
}
